import { ProviderCancellations } from "./provider-cancellations";
import { isProviderCancellationUnresolved } from "@/lib/provider-cancellation";
import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  Check,
  Loader2,
  PauseCircle,
  Play,
  RotateCcw,
} from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router";
import {
  type CancellationRefundStatus,
  type CancellationReturnOption,
  type PendingCancellation,
  type ProcessCancellationParams,
  useCancellationReturnOptions,
  useOverrideCancellationReturnPrice,
  usePendingCancellations,
  useProcessCancellation,
} from "@/api/orders";
import { useGetDispatchCenters } from "@/api/dispatch-centers";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { useCarrierScope } from "@/contexts/carrier-scope-context";

function formatCurrency(cents: number): string {
  return new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
  }).format(cents / 100);
}

function pendingRefundDescription(status: CancellationRefundStatus): string {
  const pending = status.lines.filter((line) => line.status === "PENDING");
  const adminCents = pending
    .filter((line) => line.approverType === "ADMIN")
    .reduce((sum, line) => sum + line.amountCents, 0);
  const providerCents = pending
    .filter((line) => line.approverType === "PROVIDER")
    .reduce((sum, line) => sum + line.amountCents, 0);
  const remaining = [
    adminCents > 0 ? `EZX authorization: ${formatCurrency(adminCents)}` : null,
    providerCents > 0
      ? `Carrier authorization: ${formatCurrency(providerCents)}`
      : null,
  ].filter((item): item is string => item !== null);
  return remaining.length > 0
    ? `Refund request: ${formatCurrency(status.amountCents)}. Still needs ${remaining.join("; ")}.`
    : `Refund request: ${formatCurrency(status.amountCents)}. Authorization is complete; settlement status is available in Refund Requests.`;
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleString("en-CA", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getTimeSince(dateString: string): string {
  const diffMinutes = Math.max(
    0,
    Math.floor((Date.now() - new Date(dateString).getTime()) / 60_000)
  );
  if (diffMinutes >= 1_440) return `${Math.floor(diffMinutes / 1_440)}d ago`;
  if (diffMinutes >= 60) return `${Math.floor(diffMinutes / 60)}h ago`;
  return `${diffMinutes}m ago`;
}

function operationalPhase(shipment: PendingCancellation): string {
  if (shipment.custody?.requiresDisposition) return "In custody";
  if (shipment.tasks?.some((task) => task.status === "IN_PROGRESS"))
    return "Active dispatch";
  if (shipment.tasks?.some((task) => task.tripId)) return "Planned";
  return "Awaiting planning";
}

function taskContext(shipment: PendingCancellation): string {
  const tasks = shipment.tasks ?? [];
  const trips = new Set(
    tasks.flatMap((task) => (task.tripId ? [task.tripId] : []))
  );
  const stops = new Set(
    tasks.flatMap((task) => (task.waypointId ? [task.waypointId] : []))
  );
  return `${tasks.length} task${tasks.length === 1 ? "" : "s"} · ${trips.size} trip${trips.size === 1 ? "" : "s"} · ${stops.size} stop${stops.size === 1 ? "" : "s"}`;
}

function customerOf(shipment: PendingCancellation) {
  const current = shipment.order?.placerCustomer;
  const legacy = shipment.placerCustomer?.user;
  const firstName = current?.firstName ?? legacy?.firstName;
  const lastName = current?.lastName ?? legacy?.lastName;
  const email = current?.email ?? legacy?.email;
  const name =
    [firstName, lastName].filter(Boolean).join(" ") || email || "Unknown";
  return { name, email };
}

const APPROVAL_REASONS = [
  { value: "customer_request", label: "Customer request" },
  { value: "duplicate_order", label: "Duplicate order" },
  { value: "address_issue", label: "Address issue" },
  { value: "other", label: "Other (custom)" },
];

const RESUME_REASONS = [
  { value: "request_declined", label: "Cancellation request declined" },
  { value: "customer_retracted", label: "Customer retracted request" },
  { value: "service_must_continue", label: "Service must continue" },
  { value: "other", label: "Other (custom)" },
];

type ResolutionAction = "approve" | "reject";
type Disposition = "RETURN_TO_SENDER" | "HOLD_AT_DEPOT" | "";
type ReturnPayer = "CUSTOMER" | "CARRIER" | "EZX" | "WAIVED";

function optionAmounts(
  option: CancellationReturnOption,
  override?: { totalChargeCents: number; taxCents: number }
) {
  const total = override?.totalChargeCents ?? option.returnChargeCents;
  const tax = override?.taxCents ?? option.taxCents;
  return { total, tax, net: total - tax };
}

function ProcessCancellationDialog({
  shipment,
  open,
  onOpenChange,
  action,
}: {
  shipment: PendingCancellation | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  action: ResolutionAction;
}) {
  const navigate = useNavigate();
  const processCancellation = useProcessCancellation();
  const quoteReturns = useCancellationReturnOptions();
  const overridePrice = useOverrideCancellationReturnPrice();
  const { data: centers = [] } = useGetDispatchCenters();
  const [refundType, setRefundType] = useState<"full" | "none">("full");
  const [selectedReason, setSelectedReason] = useState("");
  const [customReason, setCustomReason] = useState("");
  const [notes, setNotes] = useState("");
  const [tasksToRetry, setTasksToRetry] = useState<string[]>([]);
  const [providerReference, setProviderReference] = useState("");
  const [receiptEvidenceReason, setReceiptEvidenceReason] = useState("");
  const [nonReceiptReason, setNonReceiptReason] = useState("");
  const [confirmedNotReceived, setConfirmedNotReceived] = useState(false);
  const [disposition, setDisposition] = useState<Disposition>("");
  const [holdCenterId, setHoldCenterId] = useState("");
  const [returnOptions, setReturnOptions] = useState<
    CancellationReturnOption[]
  >([]);
  const [returnTransportId, setReturnTransportId] = useState("");
  const [payer, setPayer] = useState<ReturnPayer>("CUSTOMER");
  const [waivedCostBearer, setWaivedCostBearer] = useState<"CARRIER" | "EZX">(
    "EZX"
  );
  const [externalInterceptReference, setExternalInterceptReference] =
    useState("");
  const [overrideAmount, setOverrideAmount] = useState("");
  const [overrideReason, setOverrideReason] = useState("");
  const [overrides, setOverrides] = useState<
    Record<string, { totalChargeCents: number; taxCents: number }>
  >({});

  const custody = shipment?.custody;
  const requiresDisposition = custody?.requiresDisposition === true;
  const requiresExternalIntercept =
    requiresDisposition &&
    (custody.custody.kind === "SHIPMENT_STATUS" ||
      custody.custody.kind === "PROVIDER_EVENT" ||
      custody.custody.kind === "EXTERNAL_HANDOFF");
  const custodyCarrierId = requiresDisposition
    ? custody.custody.carrierId
    : null;
  const custodyCenterId =
    requiresDisposition && "dispatchCenterId" in custody.custody
      ? custody.custody.dispatchCenterId
      : null;
  const returnCenters = centers.filter(
    (center) => center.isActive && center.canHandleReturn === true
  );

  useEffect(() => {
    setRefundType("full");
    setSelectedReason("");
    setCustomReason("");
    setNotes("");
    setTasksToRetry([]);
    setDisposition("");
    setNonReceiptReason("");
    setProviderReference("");
    setReceiptEvidenceReason("");
    setConfirmedNotReceived(false);
    setReturnOptions([]);
    setReturnTransportId("");
    setPayer("CUSTOMER");
    setWaivedCostBearer("EZX");
    setExternalInterceptReference("");
    setOverrideAmount("");
    setOverrideReason("");
    setOverrides({});
    setHoldCenterId(custodyCenterId ?? "");
  }, [shipment?.id, action, open, custodyCenterId]);

  const payment = shipment?.order?.payments?.[0] ?? shipment?.payments?.[0];
  const grossRefund =
    shipment?.refundInfo?.totalRefund ?? payment?.amountCents ?? 0;
  const isNetTerms = payment?.method === "INVOICE";
  const selectedReturn = returnOptions.find(
    (option) => option.returnTransportId === returnTransportId
  );
  const selectedAmounts = selectedReturn
    ? optionAmounts(selectedReturn, overrides[selectedReturn.returnTransportId])
    : null;
  const customerReturnCharge =
    payer === "CUSTOMER" ? (selectedAmounts?.total ?? 0) : 0;
  const netRefund = Math.max(0, grossRefund - customerReturnCharge);
  const amountDue = Math.max(0, customerReturnCharge - grossRefund);
  const reasons = action === "approve" ? APPROVAL_REASONS : RESUME_REASONS;
  const reasonLabel = reasons.find(
    (reason) => reason.value === selectedReason
  )?.label;
  const finalReason =
    selectedReason === "other" ? customReason.trim() : (reasonLabel ?? "");

  const loadReturnOptions = async () => {
    if (!shipment) return;
    try {
      const result = await quoteReturns.mutateAsync(shipment.id);
      const options = result.kind === "ok" ? (result.data?.options ?? []) : [];
      setReturnOptions(options);
      setReturnTransportId(options[0]?.returnTransportId ?? "");
      if (options.length === 0) toast.error("No return services are available");
    } catch (error: unknown) {
      const err = error as {
        response?: { data?: { error?: string } };
        message?: string;
      };
      toast.error("Could not price the return", {
        description: err.response?.data?.error ?? err.message,
      });
    }
  };

  const applyOverride = async () => {
    if (!selectedReturn || !overrideAmount || !overrideReason.trim()) {
      toast.error("Enter a return price and override reason");
      return;
    }
    const effectiveNetCents = Math.round(Number(overrideAmount) * 100);
    if (!Number.isFinite(effectiveNetCents) || effectiveNetCents < 0) {
      toast.error("Enter a valid non-negative return price");
      return;
    }
    try {
      const result = await overridePrice.mutateAsync({
        returnTransportId: selectedReturn.returnTransportId,
        effectiveNetCents,
        reason: overrideReason.trim(),
      });
      setOverrides((current) => ({
        ...current,
        [selectedReturn.returnTransportId]: result.data,
      }));
      setOverrideAmount("");
      setOverrideReason("");
      toast.success("Return price overridden");
    } catch (error: unknown) {
      const err = error as {
        response?: { data?: { error?: string } };
        message?: string;
      };
      toast.error("Could not override return price", {
        description: err.response?.data?.error ?? err.message,
      });
    }
  };

  const recordProviderReceipt = async () => {
    if (
      !shipment?.cancellationRequestedAt ||
      !providerReference.trim() ||
      !receiptEvidenceReason.trim()
    ) {
      toast.error("Enter the verified provider reference and evidence reason");
      return;
    }
    try {
      const result = await processCancellation.mutateAsync({
        shipmentId: shipment.id,
        action: "confirm_receipt",
        expectedCancellationRequestedAt: shipment.cancellationRequestedAt,
        providerReference: providerReference.trim(),
        reason: receiptEvidenceReason.trim(),
      });
      toast.success("Verified carrier custody recorded", {
        description: result.message,
      });
      onOpenChange(false);
    } catch {
      toast.error(
        "Could not record receipt evidence. Refresh the review and check the booked provider."
      );
    }
  };

  const handleSubmit = async () => {
    if (!shipment) return;
    if (action === "reject" && !selectedReason) {
      toast.error("Select why service is resuming");
      return;
    }
    if (selectedReason === "other" && !customReason.trim()) {
      toast.error("Enter a custom reason");
      return;
    }
    const reason =
      action === "approve"
        ? finalReason ||
          shipment.cancellationRequestReason ||
          "Cancellation approved"
        : finalReason;
    const params: ProcessCancellationParams = {
      shipmentId: shipment.id,
      action,
      reason,
      notes: notes.trim() || undefined,
    };

    if (action === "approve" && !requiresDisposition) {
      if (
        !confirmedNotReceived ||
        !nonReceiptReason.trim() ||
        !shipment.cancellationRequestedAt
      ) {
        toast.error("Confirm non-receipt and explain how it was checked");
        return;
      }
      params.confirmNotReceived = true;
      params.expectedCancellationRequestedAt = shipment.cancellationRequestedAt;
      params.reason = nonReceiptReason.trim();
    }
    if (action === "reject") {
      params.taskIdsToRetry =
        tasksToRetry.length > 0 ? tasksToRetry : undefined;
    } else {
      params.refundType = isNetTerms ? "none" : refundType;
      if (requiresDisposition) {
        if (!disposition) {
          toast.error("Choose what happens to the parcel in custody");
          return;
        }
        if (requiresExternalIntercept && !externalInterceptReference.trim()) {
          toast.error("Record the external intercept reference");
          return;
        }
        if (disposition === "HOLD_AT_DEPOT") {
          if (!holdCenterId) {
            toast.error("Choose a return-capable dispatch center");
            return;
          }
          params.disposition = {
            type: "HOLD_AT_DEPOT",
            dispatchCenterId: holdCenterId,
            externalInterceptReference:
              externalInterceptReference.trim() || undefined,
          };
        } else {
          if (!selectedReturn) {
            toast.error("Choose a server-priced return service");
            return;
          }
          if (payer === "CARRIER" && !custodyCarrierId) {
            toast.error("No custody carrier is available to charge");
            return;
          }
          params.disposition = {
            type: "RETURN_TO_SENDER",
            returnTransportId: selectedReturn.returnTransportId,
            expectedRequestFingerprint: selectedReturn.requestFingerprint,
            payer,
            waivedCostBearer: payer === "WAIVED" ? waivedCostBearer : undefined,
            costCarrierId:
              payer === "CARRIER" ? (custodyCarrierId ?? undefined) : undefined,
            externalInterceptReference:
              externalInterceptReference.trim() || undefined,
          };
        }
      }
    }

    try {
      const result = await processCancellation.mutateAsync(params);
      const refundStatus = result.refundStatus;
      if (action === "approve" && refundStatus) {
        toast.success("Cancellation approved — refund still pending", {
          description: pendingRefundDescription(refundStatus),
          duration: Number.POSITIVE_INFINITY,
          action: {
            label: "Review refund",
            onClick: () =>
              navigate(`/refund-requests?requestId=${refundStatus.requestId}`),
          },
        });
      } else {
        toast.success(
          action === "approve" ? "Cancellation approved" : "Service resumed",
          { description: result.message }
        );
      }
      onOpenChange(false);
    } catch (error: unknown) {
      const err = error as {
        response?: { status?: number; data?: { error?: string } };
        message?: string;
      };
      toast.error(
        isProviderCancellationUnresolved(error)
          ? "Provider cancellation needs EZX review"
          : "Could not resolve cancellation",
        {
          description: isProviderCancellationUnresolved(error)
            ? "Provider cancellation is unresolved. Check the recorded provider result in the queue before retrying. Customer refund settlement is separate."
            : err.response?.status === 409
              ? `${err.response.data?.error ?? "State changed"}. The review list was refreshed.`
              : (err.response?.data?.error ?? err.message),
        }
      );
    }
  };

  const failedTasks = (shipment?.tasks ?? []).filter(
    (task) => task.status === "FAILED"
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {action === "approve" ? "Approve cancellation" : "Resume service"}
          </DialogTitle>
          <DialogDescription>
            SHP-{shipment?.shipmentNumber} ·{" "}
            {shipment ? operationalPhase(shipment) : ""}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950">
            <div className="flex items-center gap-2 font-semibold">
              <PauseCircle className="h-4 w-4" /> Cancellation review work hold
            </div>
            <p className="mt-1 text-xs">
              While review is pending, assignment, start, completion, and
              failure actions are frozen for affected tasks. Unrelated trip work
              continues.
            </p>
          </div>

          <ProviderCancellations operations={shipment?.providerCancellations} />
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="rounded-md border p-3">
              <p className="text-xs text-muted-foreground">Requester</p>
              <p className="font-medium">
                {shipment ? customerOf(shipment).name : "—"}
              </p>
              <p className="text-xs text-muted-foreground">
                {shipment ? formatDate(shipment.cancellationRequestedAt) : "—"}
              </p>
            </div>
            <div className="rounded-md border p-3">
              <p className="text-xs text-muted-foreground">
                Operational context
              </p>
              <p className="font-medium">
                {shipment ? operationalPhase(shipment) : "—"}
              </p>
              <p className="text-xs text-muted-foreground">
                {shipment ? taskContext(shipment) : "—"}
              </p>
            </div>
          </div>

          {shipment?.cancellationRequestReason && (
            <div className="rounded-md border bg-muted/50 p-3">
              <p className="text-xs text-muted-foreground">Request reason</p>
              <p className="text-sm">{shipment.cancellationRequestReason}</p>
            </div>
          )}

          {action === "approve" && !requiresDisposition && (
            <section className="space-y-3 rounded-md border p-3">
              <p>
                Awaiting carrier receipt/confirmation. No scan does not prove
                the parcel was not received.
              </p>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={confirmedNotReceived}
                  onChange={(event) =>
                    setConfirmedNotReceived(event.target.checked)
                  }
                />
                I checked and confirm this parcel was not received
              </label>
              <label className="block">
                Non-receipt verification reason
                <textarea
                  className="w-full rounded border p-2"
                  value={nonReceiptReason}
                  onChange={(event) => setNonReceiptReason(event.target.value)}
                />
              </label>
              <div className="space-y-2 border-t pt-3">
                <p>
                  External carrier has the parcel? Record verified acceptance
                  evidence instead. Portal-operated receipts use Receive.
                </p>
                <label className="block">
                  Verified provider reference
                  <input
                    className="w-full rounded border p-2"
                    value={providerReference}
                    onChange={(event) =>
                      setProviderReference(event.target.value)
                    }
                  />
                </label>
                <label className="block">
                  Receipt evidence reason
                  <textarea
                    className="w-full rounded border p-2"
                    value={receiptEvidenceReason}
                    onChange={(event) =>
                      setReceiptEvidenceReason(event.target.value)
                    }
                  />
                </label>
                <Button
                  type="button"
                  variant="outline"
                  disabled={processCancellation.isPending}
                  onClick={() => void recordProviderReceipt()}
                >
                  Record verified carrier receipt
                </Button>
              </div>
            </section>
          )}
          {action === "approve" && requiresDisposition && (
            <div className="space-y-3 rounded-md border p-3">
              <div>
                <Label>Physical custody disposition</Label>
                <p className="text-xs text-muted-foreground">
                  Cancellation stops forward delivery; choose where the parcel
                  goes.
                </p>
              </div>
              <Select
                value={disposition}
                onValueChange={(value) => setDisposition(value as Disposition)}
              >
                <SelectTrigger aria-label="Physical custody disposition">
                  <SelectValue placeholder="Choose disposition" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="RETURN_TO_SENDER">
                    Return to sender
                  </SelectItem>
                  <SelectItem value="HOLD_AT_DEPOT">Hold at depot</SelectItem>
                </SelectContent>
              </Select>

              {disposition === "HOLD_AT_DEPOT" && (
                <div className="space-y-2">
                  <Label>Return-capable dispatch center</Label>
                  <Select value={holdCenterId} onValueChange={setHoldCenterId}>
                    <SelectTrigger aria-label="Return-capable dispatch center">
                      <SelectValue placeholder="Choose center" />
                    </SelectTrigger>
                    <SelectContent>
                      {returnCenters.map((center) => (
                        <SelectItem key={center.id} value={center.id}>
                          {center.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {disposition === "RETURN_TO_SENDER" && (
                <div className="space-y-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={loadReturnOptions}
                    disabled={quoteReturns.isPending}
                  >
                    {quoteReturns.isPending ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <RotateCcw className="mr-2 h-4 w-4" />
                    )}
                    Price return services
                  </Button>
                  {returnOptions.length > 0 && (
                    <>
                      <div className="space-y-2">
                        <Label>Return service</Label>
                        <Select
                          value={returnTransportId}
                          onValueChange={setReturnTransportId}
                        >
                          <SelectTrigger aria-label="Return service">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {returnOptions.map((option) => {
                              const amounts = optionAmounts(
                                option,
                                overrides[option.returnTransportId]
                              );
                              return (
                                <SelectItem
                                  key={option.returnTransportId}
                                  value={option.returnTransportId}
                                >
                                  {option.carrier.name} ·{" "}
                                  {option.carrierService.name} ·{" "}
                                  {formatCurrency(amounts.total)}
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Return cost paid by</Label>
                        <Select
                          value={payer}
                          onValueChange={(value) =>
                            setPayer(value as ReturnPayer)
                          }
                        >
                          <SelectTrigger aria-label="Return cost paid by">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="CUSTOMER">
                              Customer (default)
                            </SelectItem>
                            <SelectItem value="CARRIER">Carrier</SelectItem>
                            <SelectItem value="EZX">EZX</SelectItem>
                            <SelectItem value="WAIVED">Waived</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {payer === "WAIVED" && (
                        <div className="space-y-2">
                          <Label>Waived cost absorbed by</Label>
                          <Select
                            value={waivedCostBearer}
                            onValueChange={(value) =>
                              setWaivedCostBearer(value as "CARRIER" | "EZX")
                            }
                          >
                            <SelectTrigger aria-label="Waived cost absorbed by">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="EZX">EZX</SelectItem>
                              <SelectItem value="CARRIER">Carrier</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      {selectedAmounts && (
                        <div className="rounded-md bg-muted/50 p-3 text-sm">
                          <div className="flex justify-between">
                            <span>Gross refundable</span>
                            <span>{formatCurrency(grossRefund)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Return price</span>
                            <span>{formatCurrency(selectedAmounts.net)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Return tax</span>
                            <span>{formatCurrency(selectedAmounts.tax)}</span>
                          </div>
                          <div className="mt-1 flex justify-between border-t pt-1 font-semibold">
                            <span>Customer cash refund</span>
                            <span>{formatCurrency(netRefund)}</span>
                          </div>
                          {amountDue > 0 && (
                            <div className="flex justify-between text-amber-700">
                              <span>Customer amount due</span>
                              <span>{formatCurrency(amountDue)}</span>
                            </div>
                          )}
                        </div>
                      )}
                      <div className="rounded-md border p-3">
                        <Label>Admin price override</Label>
                        <p className="mb-2 text-xs text-muted-foreground">
                          Payment-authorized admins only. Enter pre-tax CAD and
                          record why.
                        </p>
                        <div className="grid grid-cols-[120px_1fr_auto] gap-2">
                          <Input
                            value={overrideAmount}
                            onChange={(event) =>
                              setOverrideAmount(event.target.value)
                            }
                            inputMode="decimal"
                            placeholder="0.00"
                          />
                          <Input
                            value={overrideReason}
                            onChange={(event) =>
                              setOverrideReason(event.target.value)
                            }
                            placeholder="Required reason"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            onClick={applyOverride}
                            disabled={overridePrice.isPending}
                          >
                            Apply
                          </Button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              {requiresExternalIntercept && (
                <div className="space-y-2">
                  <Label>External intercept reference</Label>
                  <Input
                    aria-label="External intercept reference"
                    value={externalInterceptReference}
                    onChange={(event) =>
                      setExternalInterceptReference(event.target.value)
                    }
                    placeholder="Carrier confirmation or case reference"
                  />
                </div>
              )}
            </div>
          )}

          {action === "approve" && isNetTerms && (
            <div className="rounded-md border bg-muted/50 p-3 text-xs text-muted-foreground">
              NET-terms order: approving voids the receivable; no cash refund is
              created.
            </div>
          )}

          {action === "approve" && payment && !isNetTerms && (
            <div className="space-y-2">
              <Label>Refund decision (separate from parcel disposition)</Label>
              <Select
                value={refundType}
                onValueChange={(value) =>
                  setRefundType(value as "full" | "none")
                }
              >
                <SelectTrigger aria-label="Refund decision">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="full">
                    Create refund request for {formatCurrency(grossRefund)}
                  </SelectItem>
                  <SelectItem value="none">No refund</SelectItem>
                </SelectContent>
              </Select>
              {refundType === "full" && (
                <p className="text-xs text-muted-foreground">
                  This creates a pending Refund Request. Money does not move
                  until its component approvals complete.
                </p>
              )}
            </div>
          )}

          <div className="space-y-2">
            <Label>
              {action === "approve"
                ? "Change reason (optional)"
                : "Resume reason"}
            </Label>
            <Select value={selectedReason} onValueChange={setSelectedReason}>
              <SelectTrigger aria-label="Resolution reason">
                <SelectValue
                  placeholder={
                    action === "approve"
                      ? "Keep customer reason"
                      : "Choose reason"
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {reasons.map((reason) => (
                  <SelectItem key={reason.value} value={reason.value}>
                    {reason.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {selectedReason === "other" && (
            <div className="space-y-2">
              <Label>Custom reason</Label>
              <Textarea
                value={customReason}
                onChange={(event) => setCustomReason(event.target.value)}
              />
            </div>
          )}
          <div className="space-y-2">
            <Label>Internal notes (optional)</Label>
            <Textarea
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
            />
          </div>

          {action === "reject" && failedTasks.length > 0 && (
            <div className="space-y-2">
              <Label>Retry failed tasks after resuming?</Label>
              {failedTasks.map((task) => (
                <label
                  key={task.id}
                  className="flex items-center gap-2 rounded-md border p-2 text-sm"
                >
                  <input
                    type="checkbox"
                    checked={tasksToRetry.includes(task.id)}
                    onChange={(event) =>
                      setTasksToRetry((current) =>
                        event.target.checked
                          ? [...current, task.id]
                          : current.filter((id) => id !== task.id)
                      )
                    }
                  />
                  {task.type.replace(/_/g, " ")}
                </label>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={processCancellation.isPending}
            variant={action === "approve" ? "destructive" : "default"}
          >
            {processCancellation.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            {action === "approve" ? "Approve cancellation" : "Resume service"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function PendingCancellations() {
  const { carrierIds, dispatchCenterIds } = useCarrierScope();
  const { data, isLoading, error } = usePendingCancellations({
    carrierIds: carrierIds.join(","),
    dispatchCenterIds: dispatchCenterIds.join(","),
  });
  const [selectedShipment, setSelectedShipment] =
    useState<PendingCancellation | null>(null);
  const [dialogAction, setDialogAction] = useState<ResolutionAction>("approve");
  const [dialogOpen, setDialogOpen] = useState(false);

  const openResolution = (
    shipment: PendingCancellation,
    action: ResolutionAction
  ) => {
    setSelectedShipment(shipment);
    setDialogAction(action);
    setDialogOpen(true);
  };

  const orders = useMemo(() => data?.data ?? [], [data]);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Pending cancellations</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {[1, 2, 3].map((item) => (
            <Skeleton key={item} className="h-16 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }
  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Pending cancellations</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-destructive">
            Failed to load pending cancellations
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" /> Pending
            cancellations
            {orders.length > 0 && (
              <Badge variant="secondary">{orders.length}</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {orders.length === 0 ? (
            <p className="py-8 text-center text-muted-foreground">
              No pending cancellation requests
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Shipment / requester</TableHead>
                  <TableHead>Operational phase</TableHead>
                  <TableHead>Held work</TableHead>
                  <TableHead>Requested</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Refundable</TableHead>
                  <TableHead className="text-right">Resolution</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.map((shipment) => {
                  const customer = customerOf(shipment);
                  return (
                    <TableRow key={shipment.id}>
                      <TableCell>
                        <p className="font-medium">
                          SHP-{shipment.shipmentNumber}
                        </p>
                        <p className="text-xs">{customer.name}</p>
                        {customer.email && (
                          <p className="text-xs text-muted-foreground">
                            {customer.email}
                          </p>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className="gap-1 border-amber-300 bg-amber-50 text-amber-800"
                        >
                          <PauseCircle className="h-3 w-3" />
                          {operationalPhase(shipment)}
                        </Badge>
                        <p className="mt-1 text-xs text-muted-foreground">
                          Cancellation review
                        </p>
                        <ProviderCancellations
                          operations={shipment.providerCancellations}
                        />
                      </TableCell>
                      <TableCell>
                        <p className="text-sm">{taskContext(shipment)}</p>
                        <p className="text-xs text-muted-foreground">
                          Forward actions frozen
                        </p>
                      </TableCell>
                      <TableCell>
                        <p className="text-sm">
                          {getTimeSince(shipment.cancellationRequestedAt)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {formatDate(shipment.cancellationRequestedAt)}
                        </p>
                      </TableCell>
                      <TableCell>
                        <p className="max-w-[220px] truncate text-sm">
                          {shipment.cancellationRequestReason || "—"}
                        </p>
                      </TableCell>
                      <TableCell>
                        {shipment.refundInfo
                          ? formatCurrency(shipment.refundInfo.totalRefund)
                          : "—"}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openResolution(shipment, "reject")}
                          >
                            <Play className="mr-1 h-4 w-4" /> Resume service
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => openResolution(shipment, "approve")}
                          >
                            <Check className="mr-1 h-4 w-4" /> Approve
                            cancellation
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <ProcessCancellationDialog
        shipment={
          orders.find((item) => item.id === selectedShipment?.id) ??
          selectedShipment
        }
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        action={dialogAction}
      />
    </>
  );
}
